import React, { useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import intl from "react-intl-universal"
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { Flex, toast } from "@stardust-ds/react"
import { useForm } from "react-hook-form"
import Button from "../../../../../../atoms/Button"
import Select from "../../../../../../atoms/Select"
import ModalComponent from "../../../../components/ModalComponent"
import styles from "./index.module.scss"
import useDataApi from "../../../../../../../hooks/useDataApi"
import {
  equipmentAnalyteControlCalc,
  equipmentAnalyteLaunchRunsByControlLevel,
} from "../../../../../../../services/ciq"
import RcmpLoading from "../../../../../../atoms/Loading"
import { AlertTriangule } from "../../../../../../../assets/icons/AlertTringule"

const MIN_RUNS_TO_CALCULATE = 20 // min runs to calculated

export const schema = yup.object().shape({
  start_run: yup
    .number()
    .typeError("form.required_field")
    .required("form.required_field"),
  end_run: yup.number().typeError("form.required_field").required("form.required_field"),
})

const ModalAutomaticCalc = ({ fnOnClose, oControlLevel, fetchReleaseDetails }) => {
  const { equipmentId, analyteId } = useParams()
  const [oRunsRes, fnRunsReq] = useDataApi(equipmentAnalyteLaunchRunsByControlLevel)
  const [oCalcRes, fnCalcReq] = useDataApi(equipmentAnalyteControlCalc)

  const {
    handleSubmit,
    setValue,
    reset,
    trigger,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
  })

  useEffect(() => {
    if (!equipmentId || !analyteId || !oControlLevel) return

    const { level } = oControlLevel
    fnRunsReq(equipmentId, analyteId, level)
  }, [oControlLevel])

  useEffect(() => {
    if (oCalcRes.loading || !oCalcRes.done) return

    if (oCalcRes.data) {
      fetchReleaseDetails()
      onCloseModal()
    }

    if (oCalcRes.error) {
      const cErrorMessage = oCalcRes.error?.response?.data?.message

      if (cErrorMessage === "Minimum of 20 runs required to calculate") {
        toast({
          type: "error",
          title: intl.get(
            "ciq.release_details.levels_cards.change_calc_type_error_title",
            {
              level: oControlLevel.level,
            },
          ),
          description: intl.get(
            "ciq.release_details.automatic_calc_modal.no_runs_to_calculate_description",
          ),
        })
        return
      }

      toast({
        type: "error",
        title: intl.get("ciq.release_details.levels_cards.change_calc_type_error_title", {
          level: oControlLevel.level,
        }),
        description: intl.get(
          "ciq.release_details.levels_cards.change_calc_type_manual_average_error_description",
        ),
      })
    }
  }, [oCalcRes])

  const aRuns = oRunsRes?.data?.data || []

  const runsOptions =
    aRuns
      ?.map((oRun) => ({
        label:
          oRun?.eqpanltrun_number < 10
            ? `0${oRun?.eqpanltrun_number}`
            : String(oRun.eqpanltrun_number),
        value: oRun?.eqpanltrun_nid,
      }))
      ?.sort((a, b) => a.value - b.value) || []

  const startRunNumber = watch("start_run")
    ? runsOptions?.find((r) => r?.value === watch("start_run"))?.label
    : null

  const finalRunNumber = watch("end_run")
    ? runsOptions?.find((r) => r?.value === watch("end_run"))?.label
    : null

  const startRunsOptions = useMemo(
    () =>
      !runsOptions.length
        ? []
        : runsOptions.filter((run) =>
            finalRunNumber ? run.label < finalRunNumber : run.label,
          ),
    [finalRunNumber, runsOptions],
  )

  const endRunsOptions = useMemo(() => {
    if (!runsOptions.length) return []

    const startRunIndex = runsOptions.findIndex((run) => run.label === startRunNumber)

    return runsOptions?.slice(startRunIndex + (MIN_RUNS_TO_CALCULATE - 1))?.filter((run) =>
      startRunNumber
        ? run.label > Number(startRunNumber) + (MIN_RUNS_TO_CALCULATE - 2)
        : run.label,
    )
  }, [startRunNumber, runsOptions, startRunsOptions])

  const aRestRunsOptions = useMemo(
    () =>
      !startRunNumber || !finalRunNumber
        ? []
        : runsOptions?.filter(
            (run) =>
              Number(run.label) > Number(startRunNumber) &&
              Number(run.label) < Number(finalRunNumber),
          ),
    [runsOptions, startRunNumber, finalRunNumber],
  )

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      // handle disregard_runs min runs error field
      if (name === "disregard_runs") {
        if (!aRestRunsOptions?.length) return
        const options = value?.disregard_runs || []

        if (
          Number(aRestRunsOptions.length - options.length) <
          MIN_RUNS_TO_CALCULATE - 2
        ) {
          setError("disregard_runs", {
            type: "custom",
            message:
              "ciq.release_details.automatic_calc_modal.fields.disregard_runs.errors.max_runs",
          })
        } else {
          clearErrors("disregard_runs")
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, aRestRunsOptions])

  const bDisabledDisregardRunsSelect = useMemo(
    () =>
      !startRunNumber ||
      !finalRunNumber ||
      (aRestRunsOptions?.length || 0) <= MIN_RUNS_TO_CALCULATE - 2,
    [startRunNumber, finalRunNumber, aRestRunsOptions],
  )

  if (!oControlLevel) return null

  const { level, ncontrolid, manual_average } = oControlLevel

  const selectedStartRun = runsOptions?.find(
    (r) => Number(r.label) === manual_average?.abetweeen_run?.[0]?.eqpanltrun_number,
  )
  const selectedEndRun = runsOptions?.find(
    (r) => Number(r.label) === manual_average?.abetweeen_run?.[1]?.eqpanltrun_number,
  )
  const selectedadisregardRun = runsOptions?.filter((r) =>
    manual_average?.adisregard_run?.map(r => r?.eqpanltrun_number)?.includes(Number(r.label)),
  )

  const nMaxDisregardRunsToSelected = Number(
    (aRestRunsOptions?.length || 0) - (MIN_RUNS_TO_CALCULATE - 2),
  )

  const fnSetFieldValue = (option, fieldName) => {
    // set disenregard_runs
    if (Array.isArray(option)) {
      setValue(fieldName, option?.map((o) => o.value) || [])
      return
    }

    setValue(fieldName, option?.value || null)
    trigger(fieldName)
  }

  const submitAutomaticCalc = async (data) => {
    if (errors?.disregard_runs || oCalcRes.loading) return

    const body = {
      ctype: "MANUAL_AVERAGE",
      ...data,
    }

    if (bDisabledDisregardRunsSelect || !body?.disregard_runs) delete body.disregard_runs

    await fnCalcReq(equipmentId, analyteId, ncontrolid, body)
  }

  function onCloseModal() {
    if (oCalcRes.loading) return

    reset()
    fnOnClose?.()
  }

  const bHasRunsEnough = runsOptions.length >= MIN_RUNS_TO_CALCULATE
  const cModalTitle =
    oRunsRes.loading || bHasRunsEnough
      ? intl.get("ciq.release_details.automatic_calc_modal.title", {
          level,
        })
      : <span className={styles.centerModalTitle}>{intl.get("ciq.release_details.automatic_calc_modal.no_runs_to_calculate_title")}</span>

  return (
    <ModalComponent title={cModalTitle} fnCloseModal={onCloseModal} size="sm">
      <RcmpLoading
        bShow={oRunsRes.loading}
        size={50}
        className={styles.loadingContainer}
      />

      {!oRunsRes.loading && bHasRunsEnough && (
        <form onSubmit={handleSubmit(submitAutomaticCalc)}>
          <Flex
            width="100%"
            gap="32px"
            flexDirection="column"
            style={{ marginBottom: -32 }}
          >
            <Flex width="100%" gap="16px" flexDirection="column">
              <Select
                label={intl.get(
                  "ciq.release_details.automatic_calc_modal.fields.initial_run.label",
                )}
                className={!!errors?.start_run && styles.error}
                options={startRunsOptions}
                autoComplete="off"
                placeholder={intl.get(
                  "ciq.release_details.automatic_calc_modal.fields.initial_run.placeholder",
                )}
                defaultValue={selectedStartRun || null}
                searchable={false}
                width="100%"
                onSelect={(option) => fnSetFieldValue(option, "start_run")}
                helperText={intl.get(`${errors?.start_run?.message}`)}
              />
              <Select
                label={intl.get(
                  "ciq.release_details.automatic_calc_modal.fields.final_run.label",
                )}
                className={!!errors?.end_run && styles.error}
                options={endRunsOptions}
                autoComplete="off"
                placeholder={intl.get(
                  "ciq.release_details.automatic_calc_modal.fields.final_run.placeholder",
                )}
                defaultValue={selectedEndRun || null}
                searchable={false}
                width="100%"
                onSelect={(option) => fnSetFieldValue(option, "end_run")}
                helperText={intl.get(`${errors?.end_run?.message}`)}
              />
              <Select
                label={intl.get(
                  "ciq.release_details.automatic_calc_modal.fields.disregard_runs.label",
                )}
                className={!!errors?.disregard_runs && styles.error}
                options={aRestRunsOptions}
                autoComplete="off"
                placeholder={
                  !bDisabledDisregardRunsSelect
                    ? intl.get(
                        "ciq.release_details.automatic_calc_modal.fields.disregard_runs.placeholder",
                        { max_runs: nMaxDisregardRunsToSelected },
                      )
                    : ""
                }
                value={selectedadisregardRun || []}
                searchable={true}
                multiSelect={true}
                width="100%"
                onSelect={(options) => fnSetFieldValue(options, "disregard_runs")}
                bIsDisabled={bDisabledDisregardRunsSelect}
                helperText={intl.get(`${errors?.disregard_runs?.message}`, {
                  max_runs: nMaxDisregardRunsToSelected,
                })}
              />
            </Flex>
            <Button
              type="submit"
              className={styles.calcButton}
              isLoading={oCalcRes.loading}
            >
              {intl.get("ciq.release_details.automatic_calc_modal.calc_button")}
            </Button>
          </Flex>
        </form>
      )}

      {!oRunsRes.loading && !bHasRunsEnough && (
        <Flex
          width="100%"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap="16px"
          style={{ marginBottom: -32 }}
          className={styles.noRunsContainer}
        >
          <AlertTriangule />
          <p>
            {intl.get(
              "ciq.release_details.automatic_calc_modal.no_runs_to_calculate_description",
            )}
          </p>
          <Button type="buttom" variant="outline" onClick={onCloseModal}>
            {intl.get("ciq.release_details.automatic_calc_modal.fix_button")}
          </Button>
        </Flex>
      )}
    </ModalComponent>
  )
}

export default ModalAutomaticCalc
